<template>
  <div id="post-item-collection" class="flex-1">

    <div v-if="initialLoadFailed" class="col-span-full flex flex-col items-center justify-center py-20 px-6 text-center">
      <svg class="w-16 h-16 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
      </svg>
      <p class="text-gray-600 text-lg mb-6">{{ initialErrorMessage }}</p>
      <button @click="retryInitialLoad" class="btn btn-primary px-8 py-2 bg-primary text-white rounded-full">
        {{ $t('post_index_collection.retry') }}
      </button>
    </div>

    <template v-else-if="loading && items.length === 0 && !itemsNotFound">
      <div v-for="n in skeletonCount" :key="`sk-${n}`" class="post-item-skeleton animate-pulse">
        <div class="skeleton-image aspect-square rounded-lg bg-gray-200"></div>
        <div class="p-2 py-3 space-y-2">
          <div class="h-4 bg-gray-200 rounded w-3/4"></div>
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 bg-gray-200 rounded-full flex-shrink-0"></div>
            <div class="h-3 bg-gray-200 rounded w-24"></div>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="items && items.length > 0">
      <div v-for="(post, index) in items" :key="post.id"
           :ref="(el) => bindImpression(post.id, el, postIndexImpressionPayload(post, index))">
        <post-item :post="post" :item-index="index" :list-name="currentListName" @click='cacheItemData'></post-item>
      </div>
    </template>

    <div v-else-if="itemsNotFound" class="col-span-full text-center post-item-not-found">
      <div class="icon"></div>
      <div class="title">
        {{ $t('post_index_collection.no_matching_post') }}
      </div>
      <div>
        {{ $t('post_index_collection.check_similar_results') }}
        <span v-if="isLoggedIn">
          {{ $t('post_index_collection.or') }}
          <a
            href="#"
            class="btn btn-primary ml-6 inline-block text-14px py-1 bg-primary rounded-full"
            @click.prevent="toggleModal"
          >
            {{ $t('post_index_collection.request_model') }}
          </a>
        </span>
      </div>
      <alert-modal
        :alertState="alertState"
        :alertType="alertType"
        :alertMsg="alertMsg"
        @close-alert="closeAlert"
      ></alert-modal>
      <request-modal
        :modalState="modalState"
        @close="toggleModal"
        @alert="showAlert"
        @hide-alert="hideAlert"
      ></request-modal>
      <div class="popular-tags">
        <template
          v-if="popularTags.length > 0"
          v-for="(tag, index) in popularTags"
          :key="index"
        >
          <div v-if="tag.type == 'keyword'" class="popular-tag keyword-tag">
            <a :href="'/posts?kw=' + tag.kw" v-if="tag.index > 5" class="text-20px">{{ tag.kw }}</a>
            <a :href="'/posts?kw=' + tag.kw" v-else-if="tag.index > 2" class="text-16px">{{ tag.kw }}</a>
            <a :href="'/posts?kw=' + tag.kw" v-else class="text-14px">{{ tag.kw }}</a>
          </div>
          <div v-else class="popular-tag creator-tag">
            <a :href="'/posts?kw=' + tag.kw" v-if="tag.index > 4" class="text-20px">{{ tag.kw }}</a>
            <a :href="'/posts?kw=' + tag.kw" v-else-if="tag.index > 2" class="text-16px">{{ tag.kw }}</a>
            <a :href="'/posts?kw=' + tag.kw" v-else class="text-14px">{{ tag.kw }}</a>
          </div>
        </template>
      </div>
    </div>

    <div v-if="hasNoMoreData" class="col-span-full text-center py-4 text-sm text-gray-400">
      {{ $t('post_index_collection.no_more_posts') }}
    </div>

    <div v-else-if="hasErrorOnLoad && !loading" class="col-span-full text-center py-6 px-4">
      <p class="text-red-500 mb-3">{{ lastErrorMessage }}</p>
      <button @click="retryLoad" class="btn btn-primary px-6 py-2 bg-primary text-white rounded-full">
        {{ $t('post_index_collection.retry') }}
      </button>
    </div>

    <div v-else-if="loading && items.length > 0" class="col-span-full text-center post-item-loading">
      <img src="../../images/icons/loading.png" class="w-6 h-6 mx-auto icon loading-icon"/>
    </div>

  </div>
</template>

<script>
import 'toolcool-range-slider';
import { setWithExpiry, getWithExpiry } from "../utils/localCache";
import { useFilterBar } from "./post_index/useFilterBar";
import { usePostFetcher } from "./post_index/usePostFetcher";
import { useInfiniteScroll } from "./post_index/useInfiniteScroll";
import { useModuleImpression } from "../utils/useModuleImpression";

const CACHE_EXPIRY_MS_LONG = 120000;
const ALERT_HIDE_TIMEOUT_MS = 3000;
const FILTER_DEBOUNCE_MS = 200;

export default {
  setup() {
    // 無限捲動列表頁，事件量顧慮最大，用里程碑取樣（每 8 張才追蹤一次）。
    const { bindImpression } = useModuleImpression({ sampleEvery: 8 })
    return { bindImpression }
  },
  data() {
    return {
      cacheKey: "post_cols_cache_key",
      currPage: 0,
      autoload: true,
      loading: false,
      itemsNotFound: false,
      items: [],
      popularTags: [],
      isLoggedIn: false,
      currentParams: {},
      currentQueryString: '',
      modalState: false,
      alertState: false,
      alertType: "",
      alertMsg: "",

      fetchingPages: new Set(),
      initialLoadComplete: false,
      initialLoadFailed: false,
      initialErrorMessage: '',
      hasErrorOnLoad: false,
      lastErrorMessage: '',
      skeletonCount: 24,
    };
  },
  mounted() {
    this.setupQueryString();

    // Initialize composables
    const filterBar = useFilterBar(this);
    const postFetcher = usePostFetcher(this);
    const infiniteScroll = useInfiniteScroll({
      canLoad: () => this.autoload && this.initialLoadComplete && !this.loading && this.fetchingPages.size === 0,
      onLoad: () => postFetcher.fetchItemData(),
    });

    this._filterBar = filterBar;
    this._postFetcher = postFetcher;
    this._infiniteScroll = infiniteScroll;

    // Check cache before fetching
    const cacheData = getWithExpiry(this.cacheKey);
    if (cacheData && this.normalizeQueryParams(cacheData.params) === this.normalizeQueryParams(this.currentQueryString)) {
      this.currPage = cacheData.page;
      this.items = cacheData.data;
      this.initialLoadComplete = true;
    } else {
      window.scrollTo(0, 0);
      this.loading = true;
      postFetcher.fetchInitialData();
    }

    filterBar.setupFilterBar();
    infiniteScroll.setupScroll();

    this._popstateHandler = () => this.handlePopstate();
    window.addEventListener('popstate', this._popstateHandler);
  },
  beforeUnmount() {
    if (this._filterBar) this._filterBar.cleanupFilterListeners();
    if (this._infiniteScroll) this._infiniteScroll.cleanupScroll();
    if (this._popstateHandler) {
      window.removeEventListener('popstate', this._popstateHandler);
    }
  },
  methods: {
    postIndexImpressionPayload(post, index) {
      return {
        page_type: 'listing',
        module: 'post_index',
        landing_path: window.location.pathname,
        target_type: 'post',
        target_id: post.id,
        position: index,
      };
    },

    // --- SPA Filter / Navigation ---

    resetListState() {
      this.items = [];
      this.currPage = 0;
      this.autoload = true;
      this.loading = true;
      this.itemsNotFound = false;
      this.popularTags = [];
      this.fetchingPages = new Set();
      this.initialLoadComplete = false;
      const profileCollection = document.getElementById("profile_index_collection");
      if (profileCollection) profileCollection.classList.remove("hidden");
    },
    applyFilters() {
      clearTimeout(this._applyFiltersTimer);
      this._applyFiltersTimer = setTimeout(() => {
        this.resetListState();
        window.scrollTo(0, 0);
        const newParams = new URLSearchParams(this.currentParams).toString();
        const newUrl = window.location.origin + window.location.pathname + (newParams ? '?' + newParams : '');
        history.pushState({}, '', newUrl);
        this.currentQueryString = newParams || 'no_params';
        this._postFetcher.fetchInitialData();
      }, FILTER_DEBOUNCE_MS);
    },
    handlePopstate() {
      const currParams = new URLSearchParams(window.location.search);
      this.currentParams = Object.fromEntries(currParams);
      this.currentQueryString = window.location.search.substring(1) || 'no_params';
      this._filterBar.updateFilterBarUI();
      this.applyFilters();
    },

    // --- Retry (delegates to postFetcher) ---

    retryInitialLoad() {
      this._postFetcher.retryInitialLoad();
    },
    retryLoad() {
      this._postFetcher.retryLoad();
    },

    // --- Modal / Alert ---

    toggleModal() {
      this.modalState = !this.modalState;
    },
    cacheItemData() {
      setWithExpiry(this.cacheKey, {
        params: this.currentQueryString,
        data: this.items,
        page: this.currPage,
      }, CACHE_EXPIRY_MS_LONG);
    },
    showAlert(data) {
      this.alertState = data.alertState;
      this.alertType = data.alertType;
      this.alertMsg = data.alertMsg;
    },
    hideAlert() {
      setTimeout(() => {
        this.alertState = false;
        this.alertType = "";
      }, ALERT_HIDE_TIMEOUT_MS);
    },
    closeAlert() {
      this.alertState = false;
      this.alertType = "";
    },

    // --- Setup ---

    normalizeQueryParams(queryString) {
      if (!queryString || queryString === 'no_params') return '';
      const params = new URLSearchParams(queryString);
      const sorted = [...params.entries()].sort(([a], [b]) => a.localeCompare(b));
      return JSON.stringify(Object.fromEntries(sorted));
    },
    setupQueryString() {
      const currParams = new URLSearchParams(window.location.search);
      const paramsObject = Object.fromEntries(currParams);
      this.currentQueryString = window.location.search.substring(1) || "no_params";
      this.currentParams = paramsObject;

      if (paramsObject["page"]) this.currPage = parseInt(paramsObject["page"]);

      const tagMatch = window.location.pathname.match(/\/tags\/(\w+)/);
      if (tagMatch) {
        const slug = tagMatch[1];
        this.cacheKey = this.cacheKey + '_' + slug;
        this.currentParams["tag_slug"] = slug;
      }
    },
  },
  computed: {
    hasNoMoreData() {
      return this.items.length > 0 && !this.autoload && !this.loading;
    },
    currentListName() {
      const p = this.currentParams;
      if (p.kw) return { id: `search_results:${p.kw}`, name: 'search_results' };
      if (p.tag_slug) return { id: `tag:${p.tag_slug}`, name: 'tag' };
      if (p.c) return { id: `category:${p.c}`, name: 'category' };
      return { id: 'post_index', name: 'post_index' };
    },
  },
};
</script>
